import React, { useState, useEffect } from 'react';
import { FolderIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import Modal from './Modal';

export default function FolderSelector({ 
  projectId, 
  selectedFolderId, 
  onChange 
}) {
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folders, setFolders] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Load folders when modal is opened
  useEffect(() => {
    if (showFolderModal) {
      loadFolders();
    }
  }, [showFolderModal, currentFolder, projectId]);

  // Load selected folder name
  useEffect(() => {
    const loadSelectedFolder = async () => {
      if (selectedFolderId) {
        const { data, error } = await supabase
          .from('data_folders')
          .select('*')
          .eq('id', selectedFolderId)
          .single();
        
        if (!error && data) {
          setSelectedFolder(data);
        }
      }
    };
    loadSelectedFolder();
  }, [selectedFolderId]);

  const loadFolders = async () => {
    try {
      let query = supabase
        .from('data_folders')
        .select('*')
        .eq('project_id', projectId);

      if (currentFolder) {
        query = query.eq('parent_id', currentFolder.id);
      } else {
        query = query.is('parent_id', null);
      }

      const { data: folders, error } = await query;
      if (error) throw error;
      setFolders(folders || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const handleFolderClick = (folder) => {
    if (folder.id === currentFolder?.id) {
      // If clicking the current folder, select it
      handleSelectFolder(folder);
    } else {
      // Otherwise navigate into the folder
      setCurrentFolder(folder);
      setBreadcrumbs([...breadcrumbs, folder]);
    }
  };

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
    onChange(folder.id);
    setShowFolderModal(false);
    setCurrentFolder(null);
    setBreadcrumbs([]);
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setShowFolderModal(true)}
        className="w-full px-4 py-2 bg-dark-200 border border-divider rounded-lg hover:bg-dark-300 transition-colors flex items-center gap-2 text-left"
      >
        <FolderIcon className="w-5 h-5 text-gray-400" />
        <span className="flex-1">
          {selectedFolder ? selectedFolder.name : 'Select folder (optional)'}
        </span>
      </button>

      {showFolderModal && (
        <Modal
          isOpen={showFolderModal}
          onClose={() => {
            setShowFolderModal(false);
            setCurrentFolder(null);
            setBreadcrumbs([]);
          }}
          title="Select Folder"
        >
          <div className="p-4 space-y-4">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm">
              <button
                onClick={() => {
                  setCurrentFolder(null);
                  setBreadcrumbs([]);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Home
              </button>
              {breadcrumbs.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                  <button
                    onClick={() => {
                      setCurrentFolder(folder);
                      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {folder.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Folder List */}
            <div className="min-h-[300px] max-h-[400px] overflow-y-auto space-y-1 bg-dark-200 p-2 rounded">
              <button
                onClick={() => handleSelectFolder({ id: null, name: 'Root' })}
                className="w-full flex items-center gap-2 p-2 rounded hover:bg-dark-400 transition-colors text-left"
              >
                <FolderIcon className="w-4 h-4 text-gray-400" />
                <span>Root</span>
              </button>
              
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => handleFolderClick(folder)}
                  className={`w-full flex items-center gap-2 p-2 rounded hover:bg-dark-400 transition-colors text-left ${
                    currentFolder?.id === folder.id ? 'bg-dark-400 text-accent-primary' : ''
                  }`}
                >
                  <FolderIcon className="w-4 h-4 text-gray-400" />
                  <span className="truncate">{folder.name}</span>
                </button>
              ))}
              {folders.length === 0 && (
                <p className="text-sm text-gray-400 p-2">No folders found</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowFolderModal(false);
                  setCurrentFolder(null);
                  setBreadcrumbs([]);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSelectFolder(currentFolder)}
                className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90"
              >
                Select Location
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
} 